import React, { useCallback, useEffect, useState } from "react";
import { Button, Grid, TextField, Collapse, Typography, Box, Divider, FormControlLabel, Checkbox } from "@mui/material";
import { ExpandMore, ExpandLess, CheckBoxOutlineBlankOutlined, CheckBoxOutlined } from "@mui/icons-material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useAuth } from "hooks";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
// import { useLazyGetDDLShipmentListForWarehousingQuery } from "store/services/warehousingApi";
import { useLazyGetDDLWarehouseListQuery } from "store/services/warehouseApi";
import { useLazyGetDdlUnLoadingByUnloadingBranchQuery } from "store/services/shipmentUnLoadingApi";
import { useLazyGetDDLRouteShipmentsQuery } from "store/services/routeShipmentApi";

const INITIAL_STATE = {
  account: "",
  entity: "",
  name: "",
  description: "",
  shipmentId: [],
  warehouseId: "",
  transitBranch: "",
  unLoadedBranch: "",
  isActive: true,
  // Route fields
  route: "",
  routeStartStop: "",
  routeEndStop: "",
  routeComments: "",
};

const formatDateForName = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
};

const WarehousingForm = ({
  isBusy = null,
  warehousingDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchWarehouseId = useWatch({ control, name: "warehouseId" });
  const _watchTransitBranch = useWatch({ control, name: "transitBranch" });
  const _watchUnLoadedBranch = useWatch({ control, name: "unLoadedBranch" });
  const [showTransitBranch, setShowTransitBranch] = useState(false);
  const _watchShipmentId = useWatch({ control, name: "shipmentId" });
  const _watchRoute = useWatch({ control, name: "route" });

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  // const [fetchShipments, { data: shipments, isLoading: loadingShipments }] =
  //   useLazyGetDDLShipmentListForWarehousingQuery();
  const [fetchWarehouse, { data: warehouses, isLoading: loadingWarehouse }] =
    useLazyGetDDLWarehouseListQuery();
  const [fetchRoutes, { data: routes, isLoading: loadingRoutes }] =
    useLazyGetDDLRouteShipmentsQuery();

  // Separate API calls for transit and unloading branches
  const [
    fetchTransitBranches,
    { data: transitBranchesData, isLoading: loadingTransitBranches },
  ] = useLazyGetDdlUnLoadingByUnloadingBranchQuery();

  const [
    fetchUnloadingBranches,
    { data: unloadingBranchesData, isLoading: loadingUnloadingBranches },
  ] = useLazyGetDdlUnLoadingByUnloadingBranchQuery();

  // Local state for branches
  const [transitBranches, setTransitBranches] = useState([]);
  const [unloadingBranches, setUnloadingBranches] = useState([]);

  // Track if branches have been fetched
  const [hasTransitBranchesFetched, setHasTransitBranchesFetched] = useState(false);
  const [hasUnloadingBranchesFetched, setHasUnloadingBranchesFetched] = useState(false);

  // Track route configuration collapse state
  const [showRouteConfig, setShowRouteConfig] = useState(false);

  // Get selected branch data
  const getSelectedBranchData = useCallback(() => {
    if (_watchTransitBranch) {
      return {
        branch: _watchTransitBranch,
        type: 'transit'
      };
    } else if (_watchUnLoadedBranch) {
      return {
        branch: _watchUnLoadedBranch,
        type: 'unloading'
      };
    }
    return null;
  }, [_watchTransitBranch, _watchUnLoadedBranch]);

  // const getShipmentOptions = () => {
  //   const selectedBranchData = getSelectedBranchData();

  //   if (selectedBranchData) {
  //     // Get the appropriate branch data based on type
  //     const branchesData = selectedBranchData.type === 'transit'
  //       ? transitBranchesData
  //       : unloadingBranchesData;

  //     if (branchesData && Array.isArray(branchesData)) {
  //       const selectedBranch = branchesData.find(
  //         branch => branch._id === selectedBranchData.branch._id
  //       );

  //       if (selectedBranch && selectedBranch.shipments) {
  //         return selectedBranch.shipments.map(item => ({
  //           _id: item.shipmentData._id,
  //           shipmentId: item.shipmentData.shipmentId,
  //           status: item.shipmentData.status,
  //           isOnHold: item.shipmentData.isOnHold,
  //           isConsolidated: item.shipmentData.isConsolidated,
  //           IsReturning: false
  //         }));
  //       }
  //     }
  //   }
  //   return shipments || [];
  // };

  // Check if transit shipments are selected

  const getAutoSelectedEndStop = () => {
    if (!_watchRoute?.routeStops || _watchRoute.routeStops.length === 0) {
      return _watchTransitBranch;
    }

    const sortedStops = [..._watchRoute.routeStops].sort(
      (a, b) => (a.serialNumber || 0) - (b.serialNumber || 0)
    );

    const lastStop = sortedStops[sortedStops.length - 1];
    return lastStop?.stopDestination || _watchTransitBranch;
  };

  const getShipmentOptions = () => {
    const selectedBranchData = getSelectedBranchData();

    if (selectedBranchData) {
      const branchesData = selectedBranchData.type === 'transit'
        ? transitBranchesData
        : unloadingBranchesData;

      if (branchesData && Array.isArray(branchesData)) {
        const selectedBranch = branchesData.find(
          branch => branch._id === selectedBranchData.branch._id
        );

        if (selectedBranch && selectedBranch.shipments) {
          return selectedBranch.shipments.map(item => ({
            _id: item.shipmentData._id,
            shipmentId: item.shipmentData.shipmentId,
            status: item.shipmentData.status,
            isOnHold: item.shipmentData.isOnHold,
            isConsolidated: item.shipmentData.isConsolidated,
            IsReturning: false
          }));
        }
      }
    }
    return [];
  };

  const hasTransitShipments = useCallback(() => {
    const selectedBranchData = getSelectedBranchData();
    return selectedBranchData?.type === 'transit' &&
      _watchShipmentId &&
      Array.isArray(_watchShipmentId) &&
      _watchShipmentId.length > 0;
  }, [_watchShipmentId, getSelectedBranchData]);

  const handleOnSubmit = (data) => {
    const payload = {
      ...data,
      entity: data?.entity?._id,
      account: data?.account?._id,
      warehouseId: data?.warehouseId?._id,
      transitBranch: data?.transitBranch?._id,
      unLoadedBranch: data?.unLoadedBranch?._id,
      shipmentId: data?.shipmentId?.map((s) => s?._id),
    };

    // Add route data if transit shipments are selected
    if (hasTransitShipments()) {
      payload.route = data?.route?._id;
      payload.routeStartStop = data?.routeStartStop?._id;

      // Auto-select end stop if route is selected but end stop is not manually selected
      if (data?.route && !data?.routeEndStop) {
        const autoEndStop = getAutoSelectedEndStop();
        payload.routeEndStop = autoEndStop?._id;
      } else {
        payload.routeEndStop = data?.routeEndStop?._id;
      }

      payload.routeComments = data?.routeComments || "";
    }

    onSubmit(payload);
  };

  // Handle branch selection with mutual exclusion
  const handleTransitBranchChange = (data) => {
    if (data) {
      setValue("unLoadedBranch", "");
    }
    setValue("transitBranch", data);
    setValue("shipmentId", []);
    // Clear route fields when transit branch changes
    setValue("route", "");
    setValue("routeStartStop", "");
    setValue("routeEndStop", "");
    setValue("routeComments", "");
  };

  const handleUnLoadedBranchChange = (data) => {
    if (data) {
      setValue("transitBranch", "");
      setShowTransitBranch(false);
    }
    setValue("unLoadedBranch", data);
    setValue("shipmentId", []);
  };

  // Handle Transit Branch field click/focus
  const handleTransitBranchOpen = useCallback(() => {
    // Only fetch if we have entity and haven't fetched before
    if (_watchEntity?._id && !hasTransitBranchesFetched) {
      fetchTransitBranches({
        account: _watchAccount?._id,
        entity: _watchEntity?._id,
        type: 'transit'
      });
      setHasTransitBranchesFetched(true);
    }
  }, [_watchEntity?._id, hasTransitBranchesFetched, _watchAccount?._id, fetchTransitBranches]);

  // Handle Unloaded Branch field click/focus
  const handleUnloadedBranchOpen = useCallback(() => {
    // Only fetch if we have entity and haven't fetched before
    if (_watchEntity?._id && !hasUnloadingBranchesFetched) {
      fetchUnloadingBranches({
        account: _watchAccount?._id,
        entity: _watchEntity?._id,
        type: 'unloading'
      });
      setHasUnloadingBranchesFetched(true);
    }
  }, [_watchEntity?._id, hasUnloadingBranchesFetched, _watchAccount?._id, fetchUnloadingBranches]);

  // Auto-generate name based on warehouse
  useEffect(() => {
    if (_watchWarehouseId?.name) {
      const date = formatDateForName();
      const generatedName = `${_watchWarehouseId.name}-${date}`;
      setValue("name", generatedName);
    } else {
      setValue("name", "");
    }
  }, [_watchWarehouseId, setValue]);

  // Process transit branches data
  useEffect(() => {
    if (transitBranchesData) {
      const branchesArray = Array.isArray(transitBranchesData)
        ? transitBranchesData
        : Object.values(transitBranchesData);
      setTransitBranches(branchesArray);
    } else {
      setTransitBranches([]);
    }
  }, [transitBranchesData]);

  // Process unloading branches data
  useEffect(() => {
    if (unloadingBranchesData) {
      const branchesArray = Array.isArray(unloadingBranchesData)
        ? unloadingBranchesData
        : Object.values(unloadingBranchesData);
      setUnloadingBranches(branchesArray);
    } else {
      setUnloadingBranches([]);
    }
  }, [unloadingBranchesData]);

  // Reset fetch flags when entity changes
  useEffect(() => {
    setHasTransitBranchesFetched(false);
    setHasUnloadingBranchesFetched(false);
    setTransitBranches([]);
    setUnloadingBranches([]);
  }, [_watchEntity?._id]);

  // Set routeStartStop to transit branch when transit branch is selected
  useEffect(() => {
    if (_watchTransitBranch) {
      setValue("routeStartStop", _watchTransitBranch);
    } else {
      setValue("routeStartStop", "");
    }
  }, [_watchTransitBranch, setValue]);

  // Clear route fields when shipments change and no transit shipments
  useEffect(() => {
    if (!hasTransitShipments()) {
      setValue("route", "");
      setValue("routeEndStop", "");
      setValue("routeComments", "");
      setShowRouteConfig(false);
    }
  }, [_watchShipmentId, _watchTransitBranch, hasTransitShipments, setValue]);

  // Load warehousing detail if editing
  React.useEffect(() => {
    if (!warehousingDetail) return;
    reset(warehousingDetail);

    if (warehousingDetail.transitBranch) {
      setShowTransitBranch(true);
      handleTransitBranchOpen();
    }
    if (warehousingDetail.unLoadedBranch) {
      handleUnloadedBranchOpen();
    }
  }, [warehousingDetail, reset, handleTransitBranchOpen, handleUnloadedBranchOpen]);

  // Fetch entities when account changes
  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account?._id,
  ]);

  // Fetch general shipments (not branch-specific)
  // React.useEffect(() => {
  //   if (!_watchEntity?._id) return;
  //   if (!getSelectedBranchData()) {
  //     fetchShipments({ account: _watchAccount?._id, entity: _watchEntity?._id });
  //   }
  // }, [_watchAccount?._id, _watchEntity?._id, _watchTransitBranch, _watchUnLoadedBranch, fetchShipments]);

  // Fetch warehouses
  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchWarehouse({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchWarehouse]);

  // Fetch routes when entity changes
  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchRoutes({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchRoutes]);

  const selectedBranchData = getSelectedBranchData();



  // Get valid end stops based on selected route's routeStops
  const getValidEndStops = () => {
    if (!_watchRoute?.routeStops || !_watchTransitBranch) {
      return [];
    }

    // Sort routeStops by serialNumber to ensure correct order
    const sortedStops = [..._watchRoute.routeStops].sort(
      (a, b) => (a.serialNumber || 0) - (b.serialNumber || 0)
    );

    // Get the first stopOrigin (which should be the transit branch)
    const firstStopOrigin = sortedStops[0]?.stopOrigin?._id;

    // Collect all stopOrigin and stopDestination, excluding the first stopOrigin
    const validStops = new Set();
    sortedStops.forEach((stop) => {
      if (
        stop.stopOrigin?._id &&
        stop.stopOrigin._id !== firstStopOrigin &&
        stop.stopOrigin._id !== _watchTransitBranch?._id
      ) {
        validStops.add(JSON.stringify(stop.stopOrigin));
      }
      if (
        stop.stopDestination?._id &&
        stop.stopDestination._id !== _watchTransitBranch?._id
      ) {
        validStops.add(JSON.stringify(stop.stopDestination));
      }
    });

    return Array.from(validStops)
      .map((stop) => JSON.parse(stop))
      .filter((stop) => stop && stop._id);
  };

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                      setValue("warehouseId", "");
                      setValue("transitBranch", "");
                      setValue("unLoadedBranch", "");
                      setValue("shipmentId", []);
                      setValue("name", "");
                      setValue("route", "");
                      setValue("routeEndStop", "");
                      setValue("routeComments", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Entity"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("warehouseId", "");
                      setValue("transitBranch", "");
                      setValue("unLoadedBranch", "");
                      setValue("shipmentId", []);
                      setValue("name", "");
                      setValue("route", "");
                      setValue("routeEndStop", "");
                      setValue("routeComments", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          {/* Transit Branch Field */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showTransitBranch}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setShowTransitBranch(isChecked);
                    if (isChecked) {
                      setValue("unLoadedBranch", "");
                    }
                    if (!isChecked) {
                      setValue("transitBranch", "");
                      setValue("shipmentId", []);
                      setValue("route", "");
                      setValue("routeStartStop", "");
                      setValue("routeEndStop", "");
                      setValue("routeComments", "");
                    }
                  }}
                  icon={<CheckBoxOutlineBlankOutlined />}
                  checkedIcon={<CheckBoxOutlined />}
                />
              }
              label="Use Transit Branch"
            />
          </Grid>

          {showTransitBranch && (
            <Grid item md={6}>
              <Controller
                name="transitBranch"
                control={control}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Transit Branch"
                    multiple={false}
                    value={field.value ?? null}
                    onChange={(_, data) => handleTransitBranchChange(data)}
                    onOpen={handleTransitBranchOpen}
                    getOptionLabel={(option) =>
                      option ? `${option.name} - ${option.code}` : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={transitBranches || []}
                    loading={loadingTransitBranches}
                    loadingText="Loading Transit Branches..."
                    helperText={
                      errors?.transitBranch?.message ||
                      (transitBranches.length > 0
                        ? "Select to show shipments from transit branch"
                        : hasTransitBranchesFetched
                          ? "No transit branches available"
                          : "Click to load transit branches"
                      )
                    }
                    error={!!errors?.transitBranch}
                    disabled={!!_watchUnLoadedBranch || !_watchEntity?._id}
                  />
                )}
              />
            </Grid>
          )}

          {/* Unloaded Branch Field */}
          <Grid item md={6} hidden={showTransitBranch}>
            <Controller
              name="unLoadedBranch"
              control={control}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  label="Destination Branch"
                  multiple={false}
                  value={field.value ?? null}
                  onChange={(_, data) => handleUnLoadedBranchChange(data)}
                  onOpen={handleUnloadedBranchOpen}
                  getOptionLabel={(option) =>
                    option ? `${option.name} - ${option.code}` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={unloadingBranches || []}
                  loading={loadingUnloadingBranches}
                  loadingText="Loading destination Branches..."
                  helperText={
                    errors?.estinationBranch?.message ||
                    (unloadingBranches.length > 0
                      ? "Select to show shipments from destination branch"
                      : hasUnloadingBranchesFetched
                        ? "No destination branches available"
                        : "Click to load destination branches"
                    )
                  }
                  error={!!errors?.unLoadedBranch}
                  disabled={!!_watchTransitBranch || !_watchEntity?._id}
                />
              )}
            />
          </Grid>

          {/* Shipments Field */}
          <Grid item md={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select shipment(s)." }}
              render={({ field }) => (
                // <BaseAutocomplete
                //   {...field}
                //   fullWidth
                //   multiple
                //   label={
                //     selectedBranchData
                //       ? `${selectedBranchData.type === 'transit' ? 'Transit' : 'Unloaded'} Shipments`
                //       : "Available Shipments"
                //   }
                //   required
                //   value={field.value ?? []}
                //   onChange={(_, data) => field.onChange(data)}
                //   getOptionLabel={(option) =>
                //     option?.shipmentId
                //       ? option.IsReturning
                //         ? `${option.shipmentId} (R)`
                //         : option.shipmentId
                //       : ""
                //   }
                //   isOptionEqualToValue={(option, value) =>
                //     option?._id === value?._id
                //   }
                //   options={getShipmentOptions()}
                //   loading={
                //     selectedBranchData
                //       ? selectedBranchData.type === 'transit'
                //         ? loadingTransitBranches
                //         : loadingUnloadingBranches
                //       : loadingShipments
                //   }
                //   loadingText={
                //     selectedBranchData
                //       ? `Loading ${selectedBranchData.type} Shipments...`
                //       : "Loading Shipments..."
                //   }
                //   helperText={
                //     errors?.shipmentId?.message ||
                //     (selectedBranchData
                //       ? `Showing ${selectedBranchData.type} shipments from ${selectedBranchData.branch.name}`
                //       : "Select a branch to show branch-specific shipments, or choose from all available shipments"
                //     )
                //   }
                //   error={!!errors?.shipmentId}
                // />
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  multiple
                  label={
                    selectedBranchData
                      ? `${selectedBranchData.type === 'transit' ? 'Transit' : 'Unloaded'} Shipments`
                      : "Available Shipments"
                  }
                  required
                  value={field.value ?? []}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(option) =>
                    option?.shipmentId
                      ? option.IsReturning
                        ? `${option.shipmentId} (R)`
                        : option.shipmentId
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={getShipmentOptions()}
                  loading={
                    selectedBranchData
                      ? selectedBranchData.type === 'transit'
                        ? loadingTransitBranches
                        : loadingUnloadingBranches
                      : false
                  }
                  loadingText={
                    selectedBranchData
                      ? `Loading ${selectedBranchData.type} Shipments...`
                      : "Loading Shipments..."
                  }
                  helperText={
                    errors?.shipmentId?.message ||
                    (selectedBranchData
                      ? `Showing ${selectedBranchData.type} shipments from ${selectedBranchData.branch.name}`
                      : "Please select a Transit Branch or Unloaded Branch first"
                    )
                  }
                  error={!!errors?.shipmentId}
                  disabled={!selectedBranchData}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="warehouseId"
              control={control}
              rules={{ required: "Please select a warehouse." }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  label="Warehouse"
                  required
                  multiple={false}
                  value={field.value ?? null}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(option) =>
                    option ? `${option.name} - ${option.warehouse_id}` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={warehouses || []}
                  loading={loadingWarehouse}
                  loadingText="Loading Warehouses..."
                  helperText={errors?.warehouseId?.message}
                  error={!!errors?.warehouseId}
                />
              )}
            />
          </Grid>

          {/* Route Configuration - Collapsible Section */}
          {hasTransitShipments() && (
            <Grid item md={12}>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowRouteConfig(!showRouteConfig)}
                  endIcon={showRouteConfig ? <ExpandLess /> : <ExpandMore />}
                  sx={{
                    justifyContent: "space-between",
                    textTransform: "none",
                    fontSize: "1rem",
                    fontWeight: 600,
                    py: 1.5
                  }}
                >
                  Route Configuration (Required for Transit Shipments)
                </Button>
              </Box>

              <Collapse in={showRouteConfig}>
                <Grid container spacing={4} sx={{ mt: 0.5 }}>
                  <Grid item xs={12}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Configure the route for selected transit shipments. This route will be applied to all selected shipments.
                    </Typography>
                  </Grid>

                  {/* Route Field */}
                  <Grid item md={6}>
                    <Controller
                      name="route"
                      control={control}
                      // rules={hasTransitShipments() ? { required: "Please select a route." } : {}}
                      render={({ field }) => (
                        <BaseAutocomplete
                          {...field}
                          fullWidth
                          label="Route"
                          // required={hasTransitShipments()}
                          value={field.value ?? null}
                          onChange={(_, data) => {
                            field.onChange(data);
                            setValue("routeEndStop", null);
                          }}
                          getOptionLabel={(option) => option?.routeName ?? ""}
                          isOptionEqualToValue={(option, value) =>
                            option?._id === value?._id
                          }
                          options={routes || []}
                          loading={loadingRoutes}
                          loadingText="Loading Routes..."
                          helperText={errors?.route?.message}
                          error={!!errors?.route}
                        />
                      )}
                    />
                  </Grid>

                  {/* Route Start Stop Field */}
                  <Grid item md={6}>
                    <Controller
                      name="routeStartStop"
                      control={control}
                      rules={hasTransitShipments() ? { required: "Start stop is required." } : {}}
                      render={({ field }) => (
                        <BaseAutocomplete
                          {...field}
                          fullWidth
                          label="Start Stop"
                          required={hasTransitShipments()}
                          disabled
                          value={field.value ?? null}
                          onChange={(_, data) => field.onChange(data)}
                          getOptionLabel={(option) =>
                            option ? `${option.name} - ${option.code}` : ""
                          }
                          isOptionEqualToValue={(option, value) =>
                            option?._id === value?._id
                          }
                          options={transitBranches || []}
                          helperText={errors?.routeStartStop?.message || "Auto-filled from transit branch"}
                          error={!!errors?.routeStartStop}
                        />
                      )}
                    />
                  </Grid>

                  {/* Route End Stop Field */}
                  <Grid item md={6}>
                    <Controller
                      name="routeEndStop"
                      control={control}
                      // rules={hasTransitShipments() ? { required: "Please select an end stop." } : {}}
                      render={({ field }) => (
                        <BaseAutocomplete
                          {...field}
                          fullWidth
                          label="End Stop"
                          // required={hasTransitShipments()}
                          value={field.value ?? null}
                          onChange={(_, data) => field.onChange(data)}
                          getOptionLabel={(option) =>
                            option ? ` - ${option.code}` : ""
                          }
                          isOptionEqualToValue={(option, value) =>
                            option?._id === value?._id
                          }
                          options={getValidEndStops()}
                          loading={loadingRoutes}
                          loadingText="Loading End Stops..."
                          helperText={errors?.routeEndStop?.message}
                          error={!!errors?.routeEndStop}
                          disabled={!_watchRoute}
                        />
                      )}
                    />
                  </Grid>

                  {/* Route Comments Field */}
                  <Grid item md={6}>
                    <Controller
                      name="routeComments"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Route Comments (Optional)"
                          multiline
                          rows={2}
                          helperText={errors?.routeComments?.message}
                          error={!!errors?.routeComments}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>
          )}

          <Grid item md={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Name is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  fullWidth
                  size="small"
                  required
                  disabled
                  error={!!errors?.name}
                  helperText={errors?.name?.message || "Auto-generated from warehouse and date"}
                />
              )}
            />
          </Grid>

          <Grid item md={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  multiline
                  minRows={3}
                  fullWidth
                />
              )}
            />
          </Grid>

          {warehousingDetail && (
            <Grid item md={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(handleOnSubmit)}>
          {warehousingDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default WarehousingForm;